import React, { useState } from 'react';
import { signUp, resendSignUpCode } from '@aws-amplify/auth';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const Container = styled.div`
  min-height: 100vh;
  background-color: var(--surface);
`;

const HeroSection = styled.div`
  position: relative;
  min-height: 600px;
  background: url('/images/hero-2.jpeg') center/cover no-repeat;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 5%;
  
  @media (max-width: 768px) {
    flex-direction: column;
    justify-content: center;
    padding: 0 var(--spacing-md);
    min-height: 100vh;
    text-align: center;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      rgba(0, 0, 0, 0.8) 0%,
      rgba(0, 0, 0, 0.6) 50%,
      rgba(0, 0, 0, 0.4) 100%
    );
    z-index: 1;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 600px;
  color: white;
  
  h1 {
    font-size: 3.5rem;
    font-weight: 700;
    margin-bottom: 1.5rem;
    line-height: 1.2;
    
    @media (max-width: 768px) {
      font-size: 2.5rem;
      margin-bottom: 1rem;
    }
    
    @media (max-width: 480px) {
      font-size: 2rem;
    }
  }
  
  p {
    font-size: 1.25rem;
    margin-bottom: 2rem;
    opacity: 0.9;
    
    @media (max-width: 768px) {
      font-size: 1.1rem;
      margin-bottom: 1.5rem;
    }
    
    @media (max-width: 480px) {
      font-size: 1rem;
    }
  }
  
  @media (max-width: 768px) {
    max-width: 100%;
    margin-bottom: var(--spacing-xl);
  }
`;

const SignUpCard = styled.div`
  position: relative;
  z-index: 2;
  width: 400px;
  background: rgba(255, 255, 255, 0.95);
  padding: var(--spacing-xl);
  border-radius: var(--radius-lg);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    width: 100%;
    max-width: 400px;
    padding: var(--spacing-lg);
  }
  
  @media (max-width: 480px) {
    padding: var(--spacing-md);
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`;

const Input = styled.input`
  width: 100%;
  padding: var(--spacing-sm);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  font-size: var(--font-size-md);
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(var(--primary-rgb), 0.1);
  }
`;

const Button = styled.button`
  width: 100%;
  padding: var(--spacing-sm);
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: var(--radius-md);
  font-size: var(--font-size-md);
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: var(--primary-dark);
  }
`;

const ErrorMessage = styled.div`
  padding: var(--spacing-sm);
  background: var(--error);
  color: white;
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
`;

const LinkText = styled.div`
  text-align: center;
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
  
  a {
    color: var(--primary-color);
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const PasswordInputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const PasswordInput = styled(Input)`
  padding-right: 50px;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: var(--spacing-sm);
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-secondary);
  font-size: 1.2rem;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  
  &:hover {
    color: var(--primary-color);
  }
`;

const InfoMessage = styled.div`
  padding: var(--spacing-sm);
  background: var(--info);
  color: white;
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
  margin-top: var(--spacing-sm);
`;

const SignUp = ({ onSignUp }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            ...(name && { given_name: name }),
            ...(phoneNumber && { phone_number: phoneNumber })
          },
          autoSignIn: true
        }
      });
      onSignUp(email);
      navigate('/confirm-signup');
    } catch (error) {
      console.error('Sign up error:', error.message);
      
      // Handle specific error cases for better UX
      if (error.message.includes('already exists') || error.message.includes('already been taken')) {
        console.log('Detected existing account, attempting to resend verification code');
        // Check if it's an unconfirmed account and handle automatically
        try {
          // Note: For unconfirmed accounts, we cannot easily update the password
          // The user will need to use their original password after verification
          // We'll provide clear messaging about this
          await resendSignUpCode({ username: email });
          console.log('Successfully resent verification code, redirecting to confirm-signup');
          // Redirect to verification page with success message
          navigate(`/confirm-signup/${encodeURIComponent(email)}`, { 
            state: { 
              message: 'We found your existing account! A new verification code has been sent to your email. Please note: you will need to use your original password when signing in after verification.' 
            }
          });
        } catch (resendError) {
          console.error('Failed to resend verification code:', resendError.message);
          setError(
            'An account with this email already exists. Please sign in instead, or use a different email address.'
          );
        }
      } else if (error.message.includes('password')) {
        setError('Please ensure your password meets the requirements: at least 8 characters with uppercase, lowercase, and numbers.');
      } else {
        setError(error.message);
      }
    }
  };

  return (
    <Container>
      <HeroSection>
        <HeroContent>
          <h1>Empowering Young Minds Through Continuous Learning</h1>
          <p>Join thousands of students achieving their academic goals with PrepNest's 360° learning experience.</p>
        </HeroContent>
        
        <SignUpCard>
          <h2 style={{ marginBottom: 'var(--spacing-lg)', textAlign: 'center' }}>Create Account</h2>
          <Form onSubmit={handleSignUp}>
            {error && <ErrorMessage>{error}</ErrorMessage>}
            
            <Input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            
            <Input
              type="text"
              placeholder="Name (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            
            <Input
              type="tel"
              placeholder="Phone Number (optional)"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
            />
            
            <PasswordInputContainer>
              <PasswordInput
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <PasswordToggle
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? "👁️" : "👁"}
              </PasswordToggle>
            </PasswordInputContainer>
            
            <Button type="submit">
              Sign Up
            </Button>
            
            <LinkText>
              Already have an account? <Link to="/signin">Sign in here</Link>
            </LinkText>
          </Form>
        </SignUpCard>
      </HeroSection>

      {/* Features Section */}
      <div className="container">
        <h2 style={{ textAlign: 'center', marginBottom: 'var(--spacing-2xl)' }}>Why Choose PrepNest?</h2>
        <div className="grid" style={{ gap: 'var(--spacing-xl)' }}>
          <div className="card">
            <h3 style={{ color: 'var(--primary-color)', marginBottom: 'var(--spacing-md)' }}>
              Comprehensive Assessments
            </h3>
            <p style={{ color: 'var(--text-secondary)' }}>
              Access a wide range of carefully curated questions designed to enhance your real world problem-solving skills.
            </p>
          </div>
          <div className="card">
            <h3 style={{ color: 'var(--primary-color)', marginBottom: 'var(--spacing-md)' }}>
              Continuous Growth
            </h3>
            <p style={{ color: 'var(--text-secondary)' }}>
              Gain insights from every assessment to drive continuous learning and long-term growth.
            </p>
          </div>
          <div className="card">
            <h3 style={{ color: 'var(--primary-color)', marginBottom: 'var(--spacing-md)' }}>
              Expert Support
            </h3>
            <p style={{ color: 'var(--text-secondary)' }}>
              Our team of experienced educators is here to guide you through your learning journey.
            </p>
          </div>
        </div>
      </div>

      {/* About Section */}
      <div style={{ 
        backgroundColor: 'white',
        padding: 'var(--spacing-2xl) 0',
        marginTop: 'var(--spacing-2xl)'
      }}>
        <div className="container">
          <div style={{ 
            maxWidth: '800px',
            margin: '0 auto',
            textAlign: 'center'
          }}>
            <h2 style={{ marginBottom: 'var(--spacing-xl)' }}>About PrepNest</h2>
            <p style={{ 
              color: 'var(--text-secondary)',
              lineHeight: 1.6,
              fontSize: 'var(--font-size-lg)',
              marginBottom: 'var(--spacing-xl)'
            }}>
              At PrepNest, we're transforming the way students learn—combining technology, innovation, and expert guidance to support continuous, meaningful education.
              Our mission goes beyond exam preparation. We aim to nurture a deep understanding of concepts and their real-world applications, empowering students to learn for life, not just for exams.
              <br /><br />
              Quality learning experiences, accessible to all—that's our promise.
            </p>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 'var(--spacing-xl)',
              textAlign: 'center'
            }}>
              <div>
                <div style={{ 
                  fontSize: '2rem', 
                  color: 'var(--primary-color)', 
                  marginBottom: 'var(--spacing-sm)'
                }}>
                  🎯
                </div>
                <h3 style={{ 
                  color: 'var(--primary-color)', 
                  marginBottom: 'var(--spacing-xs)',
                  fontSize: 'var(--font-size-lg)'
                }}>
                  Personalized Learning
                </h3>
                <p style={{ 
                  color: 'var(--text-secondary)',
                  fontSize: 'var(--font-size-md)'
                }}>
                  Adaptive tests tailored to your learning pace
                </p>
              </div>
              <div>
                <div style={{ 
                  fontSize: '2rem', 
                  color: 'var(--primary-color)', 
                  marginBottom: 'var(--spacing-sm)'
                }}>
                  💡
                </div>
                <h3 style={{ 
                  color: 'var(--primary-color)', 
                  marginBottom: 'var(--spacing-xs)',
                  fontSize: 'var(--font-size-lg)'
                }}>
                  Continuous Growth
                </h3>
                <p style={{ 
                  color: 'var(--text-secondary)',
                  fontSize: 'var(--font-size-md)'
                }}>
                  Regular updates with new content and features
                </p>
              </div>
              <div>
                <div style={{ 
                  fontSize: '2rem', 
                  color: 'var(--primary-color)', 
                  marginBottom: 'var(--spacing-sm)'
                }}>
                  🤝
                </div>
                <h3 style={{ 
                  color: 'var(--primary-color)', 
                  marginBottom: 'var(--spacing-xs)',
                  fontSize: 'var(--font-size-lg)'
                }}>
                  Expert Support
                </h3>
                <p style={{ 
                  color: 'var(--text-secondary)',
                  fontSize: 'var(--font-size-md)'
                }}>
                  Dedicated team to assist your learning journey
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Container>
  );
};

export default SignUp;

//import React, { useState } from 'react';
//import { signUp } from '@aws-amplify/auth';
//import { useNavigate } from 'react-router-dom';
//
//const SignUp = ({ onSignUp }) => {
//  const [username, setUsername] = useState('');
//  const [password, setPassword] = useState('');
//  const [email, setEmail] = useState('');
//  const [error, setError] = useState('');
//  const navigate = useNavigate();
//
//  const handleSignUp = async (e) => {
//    e.preventDefault();
//    try {
//      await signUp({
//        username,
//        password,
//        options: {
//          userAttributes: {
//            email,
//          },
//          autoSignIn: true
//        }
//      });
//      onSignUp(username);
//      navigate('/confirm-signup');
//    } catch (error) {
//      setError(error.message);
//    }
//  };
//
//  return (
//    <form onSubmit={handleSignUp}>
//      <h2>Sign Up</h2>
//      {error && <p style={{ color: 'red' }}>{error}</p>}
//      <input
//        type="text"
//        placeholder="Username"
//        value={username}
//        onChange={(e) => setUsername(e.target.value)}
//      />
//      <input
//        type="password"
//        placeholder="Password"
//        value={password}
//        onChange={(e) => setPassword(e.target.value)}
//      />
//      <input
//        type="email"
//        placeholder="Email"
//        value={email}
//        onChange={(e) => setEmail(e.target.value)}
//      />
//      <button type="submit">Sign Up</button>
//    </form>
//  );
//};
//
//export default SignUp;